/* Logo Enrichment — Run History (Iter 39 Phase 2).

   Lists every enrichment run from the Iter 39 backend with operator
   filters, recovery-state visibility, pagination and per-row drill-in
   to the Run Detail page.

   Backend: GET /api/data/admin/logos/enrich
   See routes_enrichment_runs.py for the response shape. The page
   gracefully renders both legacy runs (pre-Iter 39, no persisted
   outcomes) and new runs (sample_outcomes_persisted=true) — only the
   "Outcomes persisted" badge differs. */

import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
    Play, ArrowSquareOut, CheckCircle, XCircle, Clock,
    CircleNotch, WarningCircle, Pause, Plug, ArrowsClockwise,
    Info, ClipboardText,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";

const PAGE_SIZE = 25;

// Iter 39 — full operational-status enum (operator directive 2026-02-10).
const STATUS_OPTIONS = [
    { value: "", label: "Any status" },
    { value: "queued", label: "Queued" },
    { value: "running", label: "Running" },
    { value: "completed", label: "Completed" },
    { value: "partial", label: "Partial" },
    { value: "interrupted", label: "Interrupted" },
    { value: "timed_out", label: "Timed out" },
    { value: "failed", label: "Failed" },
];

const MODE_OPTIONS = [
    { value: "", label: "Any mode" },
    { value: "dry_run", label: "Dry-run" },
    { value: "live", label: "Skip Review Queue" },
];

const SORT_OPTIONS = [
    { value: "started_at_desc", label: "Started — newest first" },
    { value: "started_at_asc", label: "Started — oldest first" },
    { value: "duration_desc", label: "Duration — longest first" },
    { value: "cohort_desc", label: "Cohort — largest first" },
];

function RunStatusBadge({ status, recoverable }) {
    const map = {
        queued: { Icon: Clock, cls: "bg-slate-100 text-slate-700", label: "Queued" },
        running: { Icon: CircleNotch, cls: "bg-blue-100 text-blue-700", label: "Running", spin: true },
        completed: { Icon: CheckCircle, cls: "bg-emerald-100 text-emerald-700", label: "Completed" },
        failed: { Icon: XCircle, cls: "bg-rose-100 text-rose-700", label: "Failed" },
        partial: { Icon: Pause, cls: "bg-amber-100 text-amber-800", label: "Partial" },
        interrupted: { Icon: Plug, cls: "bg-amber-100 text-amber-800", label: "Interrupted" },
        timed_out: { Icon: WarningCircle, cls: "bg-amber-100 text-amber-800", label: "Timed out" },
    };
    const entry = map[status] || map.queued;
    const Icon = entry.Icon;
    return (
        <div className="inline-flex items-center gap-1.5">
            <span
                data-testid={`run-status-${status}`}
                className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium ${entry.cls}`}
            >
                <Icon size={11} weight="fill" className={entry.spin ? "animate-spin" : ""} />
                {entry.label}
            </span>
            {recoverable && (
                <span
                    data-testid="recoverable-badge"
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide bg-indigo-100 text-indigo-700 font-semibold"
                    title="A subset of this run's cohort can be re-attempted via Re-cohort."
                >
                    <ArrowsClockwise size={10} weight="bold" />
                    Recoverable
                </span>
            )}
        </div>
    );
}

function ModeBadge({ mode }) {
    if (mode === "live") {
        return (
            <span data-testid="run-mode-live" className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-slate-200 text-slate-700 font-semibold">
                SKIP REVIEW
            </span>
        );
    }
    return (
        <span data-testid="run-mode-dry-run" className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
            DRY-RUN
        </span>
    );
}

function formatTs(ts) {
    if (!ts) return "—";
    try {
        const d = new Date(ts);
        return d.toLocaleString();
    } catch {
        return ts;
    }
}

function formatDuration(ms) {
    if (!ms || ms <= 0) return "—";
    const s = Math.round(ms / 1000);
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const rs = s % 60;
    if (m < 60) return `${m}m ${rs}s`;
    const h = Math.floor(m / 60);
    const rm = m % 60;
    return `${h}h ${rm}m`;
}

export default function LogoEnrichmentRuns() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({ total: 0, page: 1, page_size: PAGE_SIZE, items: [] });

    // Logo Enrichment v2 UX polish (iter 86) — page-load-only fetch of
    // global Review Queue stats so the runs index shows a "Pending
    // Reviews" shortcut at the top. No polling per operator directive.
    const [reviewStats, setReviewStats] = useState(null);
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data: resp } = await api.get("/data/admin/logos/review-queue-stats");
                if (cancelled) return;
                setReviewStats(resp);
            } catch {
                // Optional UI; ignore silently on failure.
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const status = searchParams.get("status") || "";
    const mode = searchParams.get("mode") || "";
    const since = searchParams.get("since") || "";
    const until = searchParams.get("until") || "";
    const triggeredBy = searchParams.get("triggered_by") || "";
    const sort = searchParams.get("sort") || "started_at_desc";
    const page = Math.max(1, parseInt(searchParams.get("page") || "1", 10));

    const updateParam = (key, value) => {
        const next = new URLSearchParams(searchParams);
        if (value === "" || value === null || value === undefined) {
            next.delete(key);
        } else {
            next.set(key, value);
        }
        // Any filter change resets page → 1.
        if (key !== "page") next.delete("page");
        setSearchParams(next);
    };

    const clearFilters = () => {
        setSearchParams(new URLSearchParams());
    };

    const queryString = useMemo(() => {
        const params = new URLSearchParams();
        params.set("page", String(page));
        params.set("page_size", String(PAGE_SIZE));
        params.set("sort", sort);
        if (status) params.set("status", status);
        if (mode) params.set("mode", mode);
        if (since) params.set("since", since);
        if (until) params.set("until", until);
        if (triggeredBy) params.set("triggered_by", triggeredBy);
        return params.toString();
    }, [page, sort, status, mode, since, until, triggeredBy]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const { data: resp } = await api.get(`/data/admin/logos/enrich?${queryString}`);
                if (cancelled) return;
                setData(resp);
            } catch (err) {
                if (cancelled) return;
                toast.error(formatApiError(err, "Failed to load enrichment runs"));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [queryString]);

    const pageCount = Math.max(1, Math.ceil((data.total || 0) / PAGE_SIZE));
    const hasFilters = !!(status || mode || since || until || triggeredBy);

    // Identify the first row with pending reviews for the "Open Review Queue"
    // shortcut so the operator lands on a run where work is waiting.
    const firstRunWithPending = useMemo(() => {
        return (data.items || []).find((r) => (r.pending_review_count || 0) > 0);
    }, [data.items]);

    return (
        <div data-testid="logo-enrichment-runs-page">
            <PageHeader
                eyebrow="Data · Company Repository"
                title="Logo Enrichment — Run History"
                description="Every enrichment run with operator filters, recovery state and re-cohort. Click a run to inspect per-row outcomes, approve candidates from the Review tab, export CSV, or build a re-cohort."
                actions={(
                    <Link to="/data/logo-enrichment">
                        <Button variant="outline" size="sm" className="gap-2" data-testid="launcher-btn">
                            <Play size={14} weight="fill" />
                            Open Launcher
                        </Button>
                    </Link>
                )}
            />

            <div className="px-6 sm:px-8 py-6 space-y-6 max-w-7xl">

                {/* Logo Enrichment v2 UX polish (iter 86) — Review Queue
                    informational banner. Replaces previous technical
                    red warning. Tone: blue / informational. */}
                <div
                    className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 flex items-start gap-3"
                    data-testid="review-queue-info-banner"
                >
                    <Info size={20} weight="fill" className="text-blue-600 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-blue-900">
                            Review Queue enabled
                        </div>
                        <div className="text-xs text-blue-800 mt-1 leading-relaxed">
                            Logo enrichment results now require human verification before being added to the Company Repository.
                            Run enrichment normally and approve results from the Review tab on each run.
                        </div>
                    </div>
                </div>

                {/* Logo Enrichment v2 UX polish (iter 86) — Pending
                    Reviews shortcut. Page-load fetch only. */}
                {reviewStats && (reviewStats.pending_review || 0) > 0 && (
                    <div
                        className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 flex items-center gap-3"
                        data-testid="pending-reviews-shortcut"
                    >
                        <ClipboardText size={20} weight="fill" className="text-amber-700 shrink-0" />
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-amber-900">
                                <span data-testid="pending-reviews-count">{reviewStats.pending_review.toLocaleString()}</span> logo
                                {reviewStats.pending_review === 1 ? "" : "s"} waiting for verification
                            </div>
                            <div className="text-xs text-amber-800 mt-0.5">
                                {firstRunWithPending
                                    ? "Jump to the most recent run with pending reviews, or click any row below."
                                    : "Open any run with a “Pending” badge below to start reviewing."}
                            </div>
                        </div>
                        {firstRunWithPending && (
                            <Link
                                to={`/data/logo-enrichment/runs/${encodeURIComponent(firstRunWithPending.run_id)}?tab=review`}
                            >
                                <Button
                                    size="sm"
                                    className="gap-2 bg-amber-600 hover:bg-amber-700 text-white"
                                    data-testid="open-pending-reviews-btn"
                                >
                                    Open Review Queue
                                    <ArrowSquareOut size={14} />
                                </Button>
                            </Link>
                        )}
                    </div>
                )}

                {/* Filters */}
                <section
                    data-testid="run-history-filters"
                    className="rounded-lg border border-slate-200 bg-white p-4"
                >
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 items-end">
                        <div>
                            <label className="block reedos-eyebrow text-slate-500 mb-1.5">Status</label>
                            <select
                                data-testid="filter-status"
                                value={status}
                                onChange={(e) => updateParam("status", e.target.value)}
                                className="w-full h-9 text-sm rounded-md border border-slate-200 bg-white px-2"
                            >
                                {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block reedos-eyebrow text-slate-500 mb-1.5">Mode</label>
                            <select
                                data-testid="filter-mode"
                                value={mode}
                                onChange={(e) => updateParam("mode", e.target.value)}
                                className="w-full h-9 text-sm rounded-md border border-slate-200 bg-white px-2"
                            >
                                {MODE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block reedos-eyebrow text-slate-500 mb-1.5">Started ≥</label>
                            <Input
                                type="datetime-local"
                                data-testid="filter-since"
                                value={since}
                                onChange={(e) => updateParam("since", e.target.value)}
                                className="h-9 text-sm"
                            />
                        </div>
                        <div>
                            <label className="block reedos-eyebrow text-slate-500 mb-1.5">Started ≤</label>
                            <Input
                                type="datetime-local"
                                data-testid="filter-until"
                                value={until}
                                onChange={(e) => updateParam("until", e.target.value)}
                                className="h-9 text-sm"
                            />
                        </div>
                        <div>
                            <label className="block reedos-eyebrow text-slate-500 mb-1.5">Triggered by</label>
                            <Input
                                type="text"
                                placeholder="email contains…"
                                data-testid="filter-triggered-by"
                                value={triggeredBy}
                                onChange={(e) => updateParam("triggered_by", e.target.value)}
                                className="h-9 text-sm"
                            />
                        </div>
                        <div>
                            <label className="block reedos-eyebrow text-slate-500 mb-1.5">Sort</label>
                            <select
                                data-testid="filter-sort"
                                value={sort}
                                onChange={(e) => updateParam("sort", e.target.value)}
                                className="w-full h-9 text-sm rounded-md border border-slate-200 bg-white px-2"
                            >
                                {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                        </div>
                    </div>
                    {hasFilters && (
                        <div className="mt-3 flex items-center justify-between">
                            <div className="text-xs text-slate-500">
                                Filters applied — showing matching runs only.
                            </div>
                            <Button
                                variant="ghost"
                                size="sm"
                                data-testid="clear-filters-btn"
                                onClick={clearFilters}
                                className="text-xs text-slate-600 hover:text-slate-900"
                            >
                                Clear filters
                            </Button>
                        </div>
                    )}
                </section>

                {/* Run table */}
                <section className="rounded-lg border border-slate-200 bg-white overflow-hidden">
                    <div className="px-4 py-3 border-b border-slate-100 text-xs text-slate-500" data-testid="run-history-summary">
                        {loading
                            ? "Loading…"
                            : <>{(data.total || 0).toLocaleString()} run{(data.total || 0) === 1 ? "" : "s"} matching</>}
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 text-left text-slate-500">
                                <tr>
                                    <th className="px-4 py-2.5 font-medium">Run</th>
                                    <th className="px-4 py-2.5 font-medium">Status</th>
                                    <th className="px-4 py-2.5 font-medium">Mode</th>
                                    <th className="px-4 py-2.5 font-medium">Started</th>
                                    <th className="px-4 py-2.5 font-medium">Duration</th>
                                    <th className="px-4 py-2.5 font-medium text-right">Cohort</th>
                                    <th className="px-4 py-2.5 font-medium text-right">Processed</th>
                                    <th className="px-4 py-2.5 font-medium text-right">Logos</th>
                                    <th className="px-4 py-2.5 font-medium text-right">Errors</th>
                                    <th className="px-4 py-2.5 font-medium">Review</th>
                                    <th className="px-4 py-2.5 font-medium">Triggered by</th>
                                    <th className="px-4 py-2.5 font-medium w-10"></th>
                                </tr>
                            </thead>
                            <tbody data-testid="run-history-rows" className="divide-y divide-slate-100">
                                {!loading && data.items.length === 0 && (
                                    <tr>
                                        <td colSpan={12} className="px-4 py-10 text-center text-slate-400" data-testid="run-history-empty">
                                            No enrichment runs match the current filters.
                                        </td>
                                    </tr>
                                )}
                                {data.items.map((r) => (
                                    <tr
                                        key={r.run_id}
                                        data-testid={`run-row-${r.run_id}`}
                                        className="hover:bg-slate-50"
                                    >
                                        <td className="px-4 py-2.5 font-mono text-xs">
                                            <Link
                                                to={`/data/logo-enrichment/runs/${encodeURIComponent(r.run_id)}`}
                                                className="text-slate-800 hover:text-blue-600 hover:underline"
                                                data-testid={`run-link-${r.run_id}`}
                                            >
                                                {r.run_id}
                                            </Link>
                                            {r.sample_outcomes_persisted && (
                                                <div className="text-[10px] text-emerald-700 mt-0.5">outcomes persisted</div>
                                            )}
                                        </td>
                                        <td className="px-4 py-2.5">
                                            <RunStatusBadge status={r.status} recoverable={r.recoverable} />
                                            {r.interrupted_reason && (
                                                <div
                                                    className="text-[10px] text-amber-700 font-mono mt-1 truncate max-w-[200px]"
                                                    title={r.interrupted_reason}
                                                    data-testid={`interrupt-reason-${r.run_id}`}
                                                >
                                                    {r.interrupted_reason}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-4 py-2.5"><ModeBadge mode={r.mode} /></td>
                                        <td className="px-4 py-2.5 text-xs text-slate-700">{formatTs(r.started_at)}</td>
                                        <td className="px-4 py-2.5 text-xs text-slate-700 font-mono tabular-nums">{formatDuration(r.duration_ms)}</td>
                                        <td className="px-4 py-2.5 text-xs text-right font-mono tabular-nums text-slate-900">{(r.cohort_total || 0).toLocaleString()}</td>
                                        <td className="px-4 py-2.5 text-xs text-right font-mono tabular-nums text-slate-700">{(r.rows_processed || 0).toLocaleString()}</td>
                                        <td className="px-4 py-2.5 text-xs text-right font-mono tabular-nums text-emerald-700">{(r.logos_acquired || 0).toLocaleString()}</td>
                                        <td className={`px-4 py-2.5 text-xs text-right font-mono tabular-nums ${r.errors_count > 0 ? "text-rose-700" : "text-slate-400"}`}>{(r.errors_count || 0).toLocaleString()}</td>
                                        {/* PROP-002 / iter 85.12 — review-queue badges.
                                            Iter 86.1 — added subtle per-row "Open Review →"
                                            text-link CTA next to the amber Pending chip so
                                            reviewers can two-click from Runs List → Review
                                            tab without opening the default Outcomes view. */}
                                        <td className="px-4 py-2.5">
                                            <div className="inline-flex items-center gap-2 flex-wrap">
                                                {(r.pending_review_count || 0) > 0 ? (
                                                    <Link
                                                        to={`/data/logo-enrichment/runs/${encodeURIComponent(r.run_id)}?tab=review`}
                                                        className="inline-flex items-center gap-1"
                                                        data-testid={`run-review-link-${r.run_id}`}
                                                        title={`Jump to the Review tab for ${r.run_id}`}
                                                    >
                                                        <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200 font-mono tabular-nums">
                                                            Pending: {r.pending_review_count}
                                                        </span>
                                                    </Link>
                                                ) : (
                                                    <span className="text-[10px] text-slate-400" data-testid={`run-review-empty-${r.run_id}`}>—</span>
                                                )}
                                                {(r.approved_count || 0) > 0 && (
                                                    <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 font-mono tabular-nums">
                                                        ✓ {r.approved_count}
                                                    </span>
                                                )}
                                                {(r.rejected_count || 0) > 0 && (
                                                    <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-rose-100 text-rose-800 font-mono tabular-nums">
                                                        ✗ {r.rejected_count}
                                                    </span>
                                                )}
                                                {(r.pending_review_count || 0) > 0 && (
                                                    <Link
                                                        to={`/data/logo-enrichment/runs/${encodeURIComponent(r.run_id)}?tab=review`}
                                                        className="text-[11px] text-cyan-700 hover:text-cyan-900 hover:underline whitespace-nowrap"
                                                        data-testid={`run-open-review-cta-${r.run_id}`}
                                                    >
                                                        Open Review →
                                                    </Link>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-2.5 text-xs text-slate-600 truncate max-w-[160px]" title={r.triggered_by || ""}>{r.triggered_by || "—"}</td>
                                        <td className="px-4 py-2.5">
                                            <Link
                                                to={`/data/logo-enrichment/runs/${encodeURIComponent(r.run_id)}`}
                                                className="text-slate-400 hover:text-slate-900 inline-flex"
                                                title="Open run detail"
                                                data-testid={`run-link-icon-${r.run_id}`}
                                            >
                                                <ArrowSquareOut size={14} />
                                            </Link>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 text-xs text-slate-500">
                        <span data-testid="run-history-pagination-summary">
                            Page {page} of {pageCount} · {(data.total || 0).toLocaleString()} total
                        </span>
                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                data-testid="run-history-page-prev"
                                disabled={page <= 1 || loading}
                                onClick={() => updateParam("page", String(page - 1))}
                            >
                                Prev
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                data-testid="run-history-page-next"
                                disabled={page >= pageCount || loading}
                                onClick={() => updateParam("page", String(page + 1))}
                            >
                                Next
                            </Button>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
}
